import React, { useContext, useMemo } from 'react';
import { 
  Box, 
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme
} from '@mui/material';
import { tableStyles } from '../theme/tableStyles';
import { InventoryContext } from './Inventory';
import { allSeeds, calculateBpPerMinute } from '../data/seedData';
import PageLayout from '../components/PageLayout';
import RarityBadge from '../components/common/RarityBadge';
import RarityAvatar from '../components/common/RarityAvatar';

const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const BestSeeds = () => {
  const theme = useTheme();
  const { inventory } = useContext(InventoryContext);

  const seedsWithBpPerMin = useMemo(() => {
    const result = [];
    
    // Flatten all seeds from all seasons
    const allSeedsFlat = [];
    const seenSeeds = new Set(); // Track unique seed IDs to prevent duplicates
    
    Object.entries(allSeeds).forEach(([season, seasonSeeds]) => {
      if (Array.isArray(seasonSeeds)) {
        seasonSeeds.forEach(seed => {
          if (seed.rarity && Array.isArray(seed.rarity)) {
            // Create an entry for each rarity of each seed
            seed.rarity.forEach(rarity => {
              const seedKey = `${seed.id}-${rarity.type}`.toLowerCase();
              
              // Only add if we haven't seen this seed-rarity combination before
              if (!seenSeeds.has(seedKey)) {
                seenSeeds.add(seedKey);
                const seedWithRarity = {
                  ...seed,
                  ...rarity, // Spread the rarity properties (type, biopoints, growTime)
                  seedName: seed.name, // Keep original seed name
                  fullId: seedKey, // Use consistent ID format
                  season: season.replace('Seeds', ''), // Add season info
                  image: `/images/seeds/${seed.id}/${rarity.type.toLowerCase()}/preview.png`
                };
                allSeedsFlat.push(seedWithRarity);
              }
            });
          }
        });
      }
    });
    
    // If we have an inventory, use it to show counts, otherwise show all seeds with count 0
    if (inventory?.seeds) {
      // Process each seed in inventory
      Object.entries(inventory.seeds).forEach(([seedId, count]) => {
        if (count > 0) {
          // Find the seed in our flattened list (case-insensitive match)
          const seed = allSeedsFlat.find(s => 
            s.fullId.toLowerCase() === seedId.toLowerCase()
          );
          
          if (seed) {
            const bpPerMin = calculateBpPerMinute(seed, seed.type);
            result.push({
              ...seed,
              count,
              bpPerMin,
              bpPerMinFormatted: bpPerMin.toFixed(2),
              image: `/images/seeds/${seed.id}/${seed.type.toLowerCase()}/preview.png`
            });
          }
        }
      });
    } else {
      // If no inventory, show all seeds with count 0
      allSeedsFlat.forEach(seed => {
        const bpPerMin = calculateBpPerMinute(seed, seed.type);
        result.push({
          ...seed,
          count: 0,
          bpPerMin,
          bpPerMinFormatted: bpPerMin.toFixed(2),
          image: `/images/seeds/${seed.id}/${seed.type.toLowerCase()}/preview.png`
        });
      });
    }
    
    // Sort by BP/m (highest first), then by biopoints for ties
    const sorted = result.sort((a, b) => {
      if (b.bpPerMin !== a.bpPerMin) {
        return b.bpPerMin - a.bpPerMin; // Sort by BP/m first
      }
      return b.biopoints - a.biopoints; // If BP/m are equal, sort by biopoints
    });
    return sorted;
  }, [inventory]);

  const hasSeedsInInventory = seedsWithBpPerMin.some(seed => seed.count > 0);
  
  return (
    <PageLayout title={hasSeedsInInventory ? "Best Seeds by BP/m" : "Available Seeds"} noCard>
      <Box sx={{ 
        width: '100%',
        minHeight: '100vh',
        pb: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '& .MuiTableCell-root': {
          textAlign: 'center',
          verticalAlign: 'middle',
          height: 60,
          minHeight: 60,
          '& .MuiAvatar-root': {
            display: 'block',
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': {
              transform: 'scale(1.1)',
              transition: 'transform 0.2s',
            }
          }
        }
      }}>
        <TableContainer 
          component={Paper} 
          sx={{ 
            ...tableStyles(theme).tableContainer,
            width: 1200,
            maxWidth: '100%',
            mt: 2,
            mb: 4,
            backgroundColor: 'transparent',
            '& .MuiTableHead-root .MuiTableCell-root': {
              py: 1,
              px: 2,
              height: 40,
              maxHeight: 40,
              padding: '8px 16px',
              backgroundColor: 'transparent',
              color: theme.palette.primary.contrastText,
              fontWeight: 'bold',
              fontSize: '0.875rem',
              borderBottom: 'none'
            },
            '& .MuiTableBody-root .MuiTableCell-root': {
              backgroundColor: 'transparent',
              color: theme.palette.text.primary,
              borderBottom: `1px solid ${theme.palette.divider}`,
              fontWeight: 500
            },
            '& .MuiTable-root': {
              backgroundColor: 'transparent',
              '&:before': {
                display: 'none'
              }
            },
            '& .MuiPaper-root': {
              backgroundColor: 'transparent',
              boxShadow: 'none'
            }
          }}
        >
          <Table>
            <TableHead sx={{
              ...tableStyles(theme).tableHead,
              backgroundColor: theme.palette.primary.main,
              '& .MuiTableCell-root': {
                color: theme.palette.primary.contrastText,
                fontWeight: 'bold',
                fontSize: '0.875rem'
              }
            }}>
              <TableRow>
                <TableCell sx={{ width: '25%', fontWeight: 'bold' }}>Seed</TableCell>
                <TableCell sx={{ width: '15%', textAlign: 'center', fontWeight: 'bold' }}>Rarity</TableCell>
                <TableCell sx={{ width: '15%', textAlign: 'center', fontWeight: 'bold' }}>Biopoints</TableCell>
                <TableCell sx={{ width: '15%', textAlign: 'center', fontWeight: 'bold' }}>Grow Time</TableCell>
                <TableCell sx={{ width: '15%', textAlign: 'center', fontWeight: 'bold' }}>BP/m</TableCell>
                <TableCell sx={{ width: '15%', textAlign: 'center', fontWeight: 'bold' }}>In Inventory</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {seedsWithBpPerMin.length > 0 ? (
                seedsWithBpPerMin.map((seed) => (
                  <TableRow key={`${seed.id}_${seed.type}`}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <RarityAvatar 
                          src={seed.image || `/images/seeds/${seed.id}/${(seed.type || 'common').toLowerCase()}/preview.png`}
                          alt={seed.name}
                          rarity={seed.type || 'Common'}
                          size={40}
                          onError={(e) => {
                            e.target.src = '/images/placeholder.png'; // Fallback image
                          }}
                        />
                        <Typography variant="body2" fontWeight={500}>
                          {seed.seedName || seed.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <RarityBadge rarity={seed.type} />
                      </Box>
                    </TableCell>
                    <TableCell>{seed.biopoints}</TableCell>
                    <TableCell>{formatTime(seed.growTime)}</TableCell>
                    <TableCell>{seed.bpPerMinFormatted}</TableCell>
                    <TableCell>{seed.count || 0}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No seeds found in your inventory.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Box sx={{ 
          mt: 4, 
          p: 3, 
          backgroundColor: 'transparent', 
          borderRadius: 1, 
          width: 1200, 
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto' 
        }}>
          <Typography variant="h6" gutterBottom>About This Page</Typography>
          <Typography variant="body2" paragraph>
            This page shows all seeds in your inventory, sorted by their Biopoints per minute (BP/m) value. 
            Higher BP/m values mean the seed is more efficient at generating biopoints over time.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>BP/m</strong> = (Biopoints per Seed) / (Grow Time in Minutes)
          </Typography>
          <Typography variant="body2">
            Limited-time seeds are marked with a star (★) and may have special properties.
          </Typography>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default BestSeeds;
